import React, { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api, { rupee } from "@/lib/api";
import CustomerLayout from "@/components/CustomerLayout";
import RecoRow from "@/components/RecoRow";
import { Spinner } from "@/components/States";
import { useCart } from "@/context/CartContext";
import { track } from "@/lib/track";
import { useSeo, productJsonLd } from "@/lib/seo";
import { Plus, Minus, Store, ChevronLeft } from "lucide-react";
import { toast } from "sonner";

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { qtyOf, addItem, inc, dec } = useCart();
  const { data: p, isLoading } = useQuery({ queryKey: ["product", id], queryFn: () => api.get(`/products/${id}`).then((r) => r.data) });
  const [variant, setVariant] = React.useState(null);

  useEffect(() => {
    if (p) track("view", { product_id: p.id, store_id: p.store?.id, category: p.category });
    setVariant(null);
  }, [p]);

  useSeo(p ? {
    title: p.name, description: p.description || `${p.name} from ${p.store?.name || "a local store"} on Grofkart`,
    image: p.image, path: `/product/${p.id}`, jsonLd: productJsonLd(p), jsonLdId: "gf-product",
  } : { title: "Product" });

  if (isLoading || !p) return <CustomerLayout><div className="flex justify-center py-20"><Spinner className="h-8 w-8" /></div></CustomerLayout>;

  const variants = p.variants || [];
  const hasVariants = variants.length > 0;
  const vLabel = (v) => [v.color, v.weight, v.size].filter(Boolean).join(" · ") || v.sku || "Variant";
  const dispPrice = variant ? (Number(variant.price) || Number(variant.mrp) || p.price) : p.price;
  const dispMrp = variant ? (Number(variant.mrp) || variant.price || p.mrp) : p.mrp;
  const dispImage = variant?.image || p.image;
  const dispStock = variant ? (variant.stock ?? 1) : (p.stock ?? 1);
  const disc = dispMrp > dispPrice ? Math.round((dispMrp - dispPrice) / dispMrp * 100) : 0;
  const qty = qtyOf(p.id);
  const add = () => {
    const item = { ...p, price: dispPrice, mrp: dispMrp, image: dispImage,
      name: variant ? `${p.name} — ${vLabel(variant)}` : p.name, variant_sku: variant?.sku };
    const res = addItem(item, p.store);
    if (!res.conflict) { track("cart_add", { product_id: p.id, store_id: p.store?.id, category: p.category }); toast.success(`${item.name} added to cart`); }
    else toast.error("Your cart has items from another store.");
  };

  return (
    <CustomerLayout>
      <button onClick={() => nav(-1)} className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-brand"><ChevronLeft className="h-4 w-4" /> Back</button>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white">
          <img src={dispImage} alt={p.name} data-testid="product-detail-image" className="h-72 w-full object-cover sm:h-96" />
        </div>
        <div>
          {p.store && <Link to={`/stores/${p.store.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-brand-soft px-3 py-1 text-xs font-bold text-brand"><Store className="h-3.5 w-3.5" /> {p.store.name}</Link>}
          <p className="mt-3 text-xs font-medium uppercase tracking-wide text-slate-400">{p.brand} · {p.unit}</p>
          <h1 data-testid="product-detail-name" className="font-head text-2xl font-extrabold text-slate-900 sm:text-3xl">{p.name}</h1>
          <div className="mt-3 flex items-center gap-3">
            <span data-testid="product-detail-price" className="font-mono text-2xl font-black text-slate-900">{rupee(dispPrice)}</span>
            {disc > 0 && <span className="font-mono text-base text-slate-400 line-through">{rupee(dispMrp)}</span>}
            {disc > 0 && <span className="rounded-md bg-amber-surge px-2 py-0.5 text-xs font-bold text-white">{disc}% OFF</span>}
          </div>

          {hasVariants && (
            <div className="mt-5" data-testid="variant-selector">
              <p className="mb-2 text-xs font-bold uppercase text-slate-400">Select variant</p>
              <div className="flex flex-wrap gap-2">
                {variants.map((v, i) => {
                  const sel = variant?.sku === v.sku && variant?.sku;
                  const oos = (v.stock ?? 1) <= 0;
                  return (
                    <button key={v.sku || i} disabled={oos} onClick={() => setVariant(sel ? null : v)} data-testid="variant-chip"
                      className={`rounded-xl border px-3 py-2 text-sm font-semibold transition-colors ${sel ? "border-brand bg-brand-soft text-brand" : "border-slate-200 text-slate-600 hover:border-brand"} ${oos ? "cursor-not-allowed opacity-40" : ""}`}>
                      {vLabel(v)}{v.price ? ` · ${rupee(Number(v.price))}` : ""}{oos ? " (out)" : ""}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {p.description && <p className="mt-4 text-sm leading-relaxed text-slate-600">{p.description}</p>}
          <div className="mt-6">
            {dispStock <= 0 ? (
              <button disabled className="w-full cursor-not-allowed rounded-xl bg-slate-200 py-3.5 text-base font-bold text-slate-400 sm:w-64">Out of stock</button>
            ) : qty === 0 ? (
              <button data-testid="product-detail-add-btn" onClick={add} className="w-full rounded-xl bg-brand py-3.5 text-base font-bold text-white hover:bg-brand-deep sm:w-64">Add to Cart</button>
            ) : (
              <div className="flex w-full items-center justify-between rounded-xl bg-brand px-4 py-3 text-white sm:w-64">
                <button onClick={() => dec(p.id)}><Minus className="h-5 w-5" /></button>
                <span className="text-lg font-bold">{qty} in cart</span>
                <button onClick={() => inc(p.id)}><Plus className="h-5 w-5" /></button>
              </div>
            )}
          </div>
        </div>
      </div>

      <RecoRow title="Frequently Bought Together" storeId={p.store?.id} productId={p.id} store={p.store} />
      <RecoRow title="You May Also Like" storeId={p.store?.id} productId={p.id} exclude={p.id} store={p.store} />
    </CustomerLayout>
  );
}
